import chunk from 'lodash/chunk';
import React, { useState } from 'react';
import { FieldError, UseFormReturn } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { showFinancialPlanningSelector } from '../../../../../AdviserArea/AdviserAreaSelector';
import { CMAType } from '../../../../../AdviserSettings/Page/CustomModelAssumptions/types';
import { PlanStartYearDTO } from '../../../../../ClientSettings/api/useClientSettingsQuery';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { Column } from '../../../../../CommonComponents/OPL/Grid/Column';
import { Row } from '../../../../../CommonComponents/OPL/Grid/Row';
import { HelperText } from '../../../../../CommonComponents/OPL/HelperText/HelperText';
import { Icon } from '../../../../../CommonComponents/OPL/Icon/Icon';
import { Toggle } from '../../../../../CommonComponents/OPL/Toggle/Toggle';
import { Tooltip } from '../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { AccountDistributionOrderModal } from '../../../../../AccountDistributionOrderModal/AccountDistributionOrderModal';
import {
  AccountDistributionStrategyIdDTO,
  useAccountDistributionOrderQuery,
} from '../../../../../AccountDistributionOrderModal/api/useAccountDistributionOptionsQuery';
import { ManualAccountsAssetAllocationStrategyIdDTO } from '../../../../../ManualAccountsAssetAllocation/types';
import { ManualAccountsAssetAllocationModal } from '../../../../../ManualAccountsAssetAllocationModal/ManualAccountsAssetAllocationModal';
import { RetirementExpenses } from '../../../../../ProfileOPL/Goals/goals/Retirement/Retirement.meta';
import { MarginProps } from '../../../../../theme/styledProps';
import { AssumptionsDTO } from '../../api/useFirmSettingsQuery';
import { SectionHeader } from '../SectionHeader/SectionHeader';
import { SettingsGrid } from '../SettingsGrid/SettingsGrid';
import { AssumptionDefinition, ASSUMPTIONS_DEFINITIONS } from './Assumptions.meta';
import { AssumptionField } from './components/AssumptionField/AssumptionField';

interface Props extends MarginProps {
  isReadonly?: boolean;
  isFirm?: boolean;
  firmAssumptions: AssumptionsDTO;
  formBag: UseFormReturn<any>;
  is3dRiskToleranceWorkflowEnabled?: boolean;
  isCustomAssumptionsEnabled?: boolean;
  onOpenCurrentYearAssumptionsModal(): void;
  onOpenExcessCashModal(): void;
}

export const Assumptions = ({
  isReadonly,
  isFirm,
  formBag,
  firmAssumptions,
  is3dRiskToleranceWorkflowEnabled,
  isCustomAssumptionsEnabled,
  onOpenCurrentYearAssumptionsModal,
  onOpenExcessCashModal,
  ...rest
}: Props) => {
  const [isAccountDistributionOrderModalOpen, setIsAccountDistributionOrderModalOpen] = useState(
    false,
  );

  const { data: accountDistributionOrder } = useAccountDistributionOrderQuery();

  const showFinancialPlanning = useSelector(showFinancialPlanningSelector);
  const [isDefaultAssetAllocationModalOpen, setIsDefaultAssetAllocationModalOpen] = useState(false);

  const fieldName = isFirm ? 'firmAssumptions' : 'adviserAssumptions';

  const { watch, setValue, formState } = formBag;

  const planStartYear = watch(`${fieldName}.planStartYear`);
  const accountDistributionStrategy = watch(`${fieldName}.accountDistributionStrategy`);
  const currentAccountDistributionOrder = watch('accountDistributionOrder');
  const excessCashFlowToSave = watch(`${fieldName}.excessCashFlowToSave`);
  const manualAccountsAssetAllocation = watch(`${fieldName}.manualAccountsAssetAllocation`);

  const selectedRetirementExpense = watch(`${fieldName}.retirementExpenses`);

  if (!showFinancialPlanning) {
    return null;
  }

  const assumptionsDefinitions: AssumptionDefinition[] = ASSUMPTIONS_DEFINITIONS.map(assumption => {
    if (assumption.subFieldName === 'riskToleranceWorkflow' && !is3dRiskToleranceWorkflowEnabled) {
      return null;
    }

    if (assumption.subFieldName === 'capitalMarketAssumptions' && isFirm) {
      return null;
    }

    if (assumption.subFieldName === 'capitalMarketAssumptions' && assumption.options) {
      return {
        ...assumption,
        options: isCustomAssumptionsEnabled
          ? assumption.options
          : assumption.options.filter(option => option.value !== CMAType.CustomCMAs),
      };
    }

    if (assumption.subFieldName === 'retirementExpenses' && assumption.options) {
      return {
        ...assumption,
        options: watch(`${fieldName}.retirementExpensesOptions`) ?? [],
      };
    }

    if (
      assumption.subFieldName === 'accountDistributionStrategy' &&
      assumption.options &&
      !isFirm
    ) {
      return {
        ...assumption,
        options: [
          ...assumption.options,
          { label: 'Use Firm Settings', value: AccountDistributionStrategyIdDTO.Inherit },
        ],
      };
    }

    if (
      assumption.subFieldName === 'manualAccountsAssetAllocation.strategy' &&
      assumption.options &&
      !isFirm
    ) {
      return {
        ...assumption,
        options: [
          ...assumption.options,
          { label: 'Use My Models', value: ManualAccountsAssetAllocationStrategyIdDTO.Model },
          { label: 'Use Firm Settings', value: ManualAccountsAssetAllocationStrategyIdDTO.Inherit },
        ],
      };
    }

    if (assumption.subFieldName === 'imposeNetInvestmentIncomeTax' && assumption.options) {
      return {
        ...assumption,
        options: watch(`${fieldName}.netInvestmentIncomeTaxOptions`) ?? [],
      };
    }

    return assumption;
  }).filter(Boolean) as AssumptionDefinition[];

  return (
    <Box as="section" {...rest}>
      <SectionHeader title="Plan Assumptions" />

      <SettingsGrid>
        {chunk(assumptionsDefinitions, 3).map(assumptionsRow => (
          <Row key={assumptionsRow[0].subFieldName} mb="32">
            {assumptionsRow.map(assumption => {
              if (
                assumption.subFieldName === 'retirementExpensesAmount' &&
                selectedRetirementExpense !== RetirementExpenses.Other
              ) {
                return null;
              }

              const manualAllocationDetailsError = (formState.errors[fieldName] as unknown as
                | { manualAccountsAssetAllocation?: { details?: FieldError } }
                | undefined)?.manualAccountsAssetAllocation?.details;

              return (
                <Column key={assumption.subFieldName} width={4}>
                  <AssumptionField
                    {...assumption}
                    fieldName={fieldName}
                    firmAssumptions={firmAssumptions}
                    formBag={formBag}
                    isFirm={isFirm}
                    isReadonly={isReadonly}
                    onChange={subFieldName => {
                      if (subFieldName === `${fieldName}.manualAccountsAssetAllocation.strategy`) {
                        setValue(`${fieldName}.manualAccountsAssetAllocation.details`, null);
                      }
                    }}
                  />
                  {assumption.subFieldName === 'planStartYear' &&
                    planStartYear === PlanStartYearDTO.CurrentYear && (
                      <Button
                        mt="8"
                        type="button"
                        variant="link"
                        leftIcon="far fa-pencil"
                        onClick={onOpenCurrentYearAssumptionsModal}
                      >
                        Manage Current Year Assumptions
                      </Button>
                    )}
                  {assumption.subFieldName === 'accountDistributionStrategy' &&
                    accountDistributionStrategy === AccountDistributionStrategyIdDTO.Custom && (
                      <Button
                        mt="8"
                        type="button"
                        variant="link"
                        leftIcon="far fa-pencil"
                        onClick={() => setIsAccountDistributionOrderModalOpen(true)}
                      >
                        Edit Distribution Order
                      </Button>
                    )}
                  {assumption.subFieldName === 'excessCashFlowToSave' &&
                    excessCashFlowToSave !== null &&
                    excessCashFlowToSave > 0 && (
                      <Button
                        mt="8"
                        type="button"
                        variant="link"
                        leftIcon="far fa-pencil"
                        onClick={onOpenExcessCashModal}
                      >
                        Manage Saved Cash Allocation
                      </Button>
                    )}
                  {assumption.subFieldName === 'manualAccountsAssetAllocation.strategy' &&
                    (manualAccountsAssetAllocation.strategy ===
                      ManualAccountsAssetAllocationStrategyIdDTO.CMA ||
                      manualAccountsAssetAllocation.strategy ===
                        ManualAccountsAssetAllocationStrategyIdDTO.Model) && (
                      <>
                        {manualAllocationDetailsError?.message && (
                          <HelperText variant="error" mt="4">
                            {manualAllocationDetailsError.message}
                          </HelperText>
                        )}
                        <Button
                          mt="8"
                          type="button"
                          variant="link"
                          leftIcon="far fa-pencil"
                          onClick={() => setIsDefaultAssetAllocationModalOpen(true)}
                        >
                          Manage Default Asset Allocation
                        </Button>
                      </>
                    )}
                </Column>
              );
            })}
          </Row>
        ))}
      </SettingsGrid>

      {accountDistributionOrder && (
        <AccountDistributionOrderModal
          isOpen={isAccountDistributionOrderModalOpen}
          onClose={() => setIsAccountDistributionOrderModalOpen(false)}
          options={accountDistributionOrder.options}
          defaultOrder={accountDistributionOrder.default}
          order={currentAccountDistributionOrder}
          onOrderChange={order =>
            setValue('accountDistributionOrder', order, { shouldDirty: true })
          }
        />
      )}

      {isDefaultAssetAllocationModalOpen && (
        <ManualAccountsAssetAllocationModal
          key={manualAccountsAssetAllocation.strategy}
          isOpen={isDefaultAssetAllocationModalOpen}
          onClose={() => setIsDefaultAssetAllocationModalOpen(false)}
          allocation={manualAccountsAssetAllocation}
          onChange={allocation =>
            setValue(`${fieldName}.manualAccountsAssetAllocation`, allocation)
          }
        />
      )}

      <Box display="flex" alignItems="center" mt="24">
        <Toggle
          {...formBag.register(`${fieldName}.enhancedReportExperience`)}
          disabled={isReadonly}
        >
          Enable Enhanced Report Generation Experience
        </Toggle>

        <Tooltip content="The enhanced experience allows you to view and save a draft report before finalization. Final reports are archived and timestamped for future access and review.">
          <Icon className="far fa-info-circle" color="neutral.500" fontSize="small" ml="4" />
        </Tooltip>
      </Box>

      <Toggle
        {...formBag.register(`${fieldName}.enableAutoGeneratedWhatIfs`)}
        disabled={isReadonly}
        mt="24"
      >
        Enable Auto-Generated What-ifs
      </Toggle>

      {isFirm && (
        <Toggle {...formBag.register('assumptionsEditableByAdviser')} mt="24">
          Allow Advisors to Override Firm Assumptions
        </Toggle>
      )}
    </Box>
  );
};
